import axios from 'axios';

// API URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_URL = process.env.REACT_APP_WS_URL || 'wss://hbkomivfqg.execute-api.us-east-1.amazonaws.com/dev';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second timeout for chart calculation
});

export const calculateChart = async (birthData) => {
  try {
    const response = await api.post('/chart/calculate', birthData);
    return response.data;
  } catch (error) {
    console.error('Error calculating chart:', error);
    throw error;
  }
};

// WebSocket-based chat message sending (no 29s timeout!)
const sendChatMessageOnce = (messages, chartData = null, useRag = true) => {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(WS_URL);
    let responseReceived = false;

    // Set a timeout for the entire operation (15 minutes)
    const timeout = setTimeout(() => {
      if (!responseReceived) {
        ws.close();
        reject(new Error('Request timeout after 15 minutes'));
      }
    }, 900000);

    // Keep the socket busy while the model thinks.
    //
    // Answers take 10-30s, and a mobile network will drop a socket that has
    // been idle for less than that. When it does, the Lambda finishes the
    // answer, finds the connection gone, and discards it — the work is done
    // and paid for, and the user sees "I ran into a problem". A small
    // periodic frame keeps the connection demonstrably alive.
    let keepAlive = null;
    const stopKeepAlive = () => {
      if (keepAlive) {
        clearInterval(keepAlive);
        keepAlive = null;
      }
    };

    ws.onopen = () => {
      console.log('WebSocket connected');
      keepAlive = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          // There is no ping route on the API, so an unknown action is used
          // deliberately: it costs nothing, and the traffic itself is the
          // point.
          try {
            ws.send(JSON.stringify({ action: 'keepalive' }));
          } catch (e) {
            // Socket is going away; the handlers below deal with it.
          }
        }
      }, 8000);
      // Send message through WebSocket
      ws.send(JSON.stringify({
        action: 'message',
        messages,
        chart_data: chartData,
        use_rag: useRag
      }));
    };

    ws.onmessage = (event) => {
      console.log('WebSocket message received');
      try {
        const data = JSON.parse(event.data);

        // Handle different message types
        if (data.type === 'error') {
          clearTimeout(timeout);
          stopKeepAlive();
          responseReceived = true;
          ws.close();
          reject(new Error(data.message || 'Unknown error'));
        } else if (data.type === 'response' || data.response) {
          clearTimeout(timeout);
          stopKeepAlive();
          responseReceived = true;
          ws.close();
          resolve(data);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        clearTimeout(timeout);
        stopKeepAlive();
        responseReceived = true;
        ws.close();
        reject(error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      clearTimeout(timeout);
      stopKeepAlive();
      if (!responseReceived) {
        responseReceived = true;
        reject(new Error('WebSocket connection error'));
      }
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      clearTimeout(timeout);
      stopKeepAlive();
      if (!responseReceived) {
        responseReceived = true;
        const err = new Error('WebSocket connection closed unexpectedly');
        err.connectionLost = true;
        reject(err);
      }
    };
  });
};

/**
 * Send a chat message, retrying once if the connection drops.
 *
 * A dropped socket is not a failed question: the model has usually finished
 * answering by then, and the old behaviour showed "I ran into a problem" for
 * what was really a flaky mobile network. One retry turns most of those into
 * an answer.
 */
export const sendChatMessage = async (messages, chartData = null, useRag = true) => {
  try {
    return await sendChatMessageOnce(messages, chartData, useRag);
  } catch (error) {
    // Only retry a lost connection. A refusal or a real error would give the
    // same result twice, and paying twice for it helps nobody.
    if (!error.connectionLost) throw error;
    console.log('Connection dropped; retrying once.');
    return sendChatMessageOnce(messages, chartData, useRag);
  }
};

// Place lookup lives in geocode.js, which merges two OSM providers so small
// towns and villages actually appear. Re-exported here to keep the existing
// import path working.
export { geocodeLocation } from './geocode';

export default api;
